import { Link } from "react-router-dom";
import { Globe2, Ship, Plane, Truck, ArrowRight, ArrowLeft, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { useLang } from "../context/LanguageContext";

const GCC = [
  { code: "AE", name_en: "UAE", name_ar: "الإمارات", flag: "🇦🇪", primary: true },
  { code: "SA", name_en: "Saudi Arabia", name_ar: "السعودية", flag: "🇸🇦" },
  { code: "KW", name_en: "Kuwait", name_ar: "الكويت", flag: "🇰🇼" },
  { code: "QA", name_en: "Qatar", name_ar: "قطر", flag: "🇶🇦" },
  { code: "BH", name_en: "Bahrain", name_ar: "البحرين", flag: "🇧🇭" },
  { code: "OM", name_en: "Oman", name_ar: "عُمان", flag: "🇴🇲" },
];

const BEYOND = [
  { code: "IQ", name_en: "Iraq", name_ar: "العراق", flag: "🇮🇶" },
  { code: "JO", name_en: "Jordan", name_ar: "الأردن", flag: "🇯🇴" },
  { code: "LB", name_en: "Lebanon", name_ar: "لبنان", flag: "🇱🇧" },
  { code: "EG", name_en: "Egypt", name_ar: "مصر", flag: "🇪🇬" },
  { code: "MA", name_en: "Morocco", name_ar: "المغرب", flag: "🇲🇦" },
  { code: "IN", name_en: "India", name_ar: "الهند", flag: "🇮🇳" },
  { code: "KE", name_en: "Kenya", name_ar: "كينيا", flag: "🇰🇪" },
  { code: "TZ", name_en: "Tanzania", name_ar: "تنزانيا", flag: "🇹🇿" },
];

export function GlobalReach() {
  const { t, lang, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const g = t.globalReach;

  return (
    <section
      className="relative py-24 bg-gradient-to-br from-cyan-50 via-white to-red-50/30 overflow-hidden"
      data-testid="global-reach"
    >
      {/* Decorative world grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 30%, #0891b2 1.5px, transparent 2px), radial-gradient(circle at 75% 60%, #ef4444 1.5px, transparent 2px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          {/* LEFT — intro */}
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] text-[#ef4444] font-semibold mb-4 uppercase">
              <Globe2 className="w-3.5 h-3.5" />
              {g.eyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-4 text-[#0891b2]">
              {g.title}
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">{g.subtitle}</p>

            {/* Logistics badges */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              {[
                { icon: Truck, label: g.logistics1 },
                { icon: Ship, label: g.logistics2 },
                { icon: Plane, label: g.logistics3 },
              ].map((l, i) => (
                <div
                  key={i}
                  data-testid={`gr-log-${i}`}
                  className="text-center bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
                >
                  <l.icon className="w-5 h-5 text-[#ef4444] mx-auto mb-2" />
                  <div className="text-[11px] font-semibold text-slate-700">{l.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Link to="/quote">
                <Button data-testid="gr-cta-quote" className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-11 px-6 font-semibold">
                  {g.ctaQuote} <Arrow className="w-4 h-4 ms-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" data-testid="gr-cta-contact" className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white h-11 px-6 font-semibold">
                  {g.ctaContact}
                </Button>
              </Link>
            </div>
          </div>

          {/* RIGHT — country cards */}
          <div className="lg:col-span-3">
            {/* GCC */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#0891b2] flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold uppercase">
                    {g.gccEyebrow}
                  </div>
                  <div className="font-display text-lg text-[#0891b2]">{g.gccTitle}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {GCC.map((c, i) => (
                  <div
                    key={c.code}
                    data-testid={`gr-gcc-${c.code}`}
                    className={`bg-white rounded-xl p-4 border transition-all hover:shadow-lg hover:-translate-y-0.5 ${
                      c.primary
                        ? "border-[#ef4444]/40 ring-2 ring-[#ef4444]/10"
                        : "border-slate-200 hover:border-[#0891b2]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl leading-none">{c.flag}</span>
                      <div className="min-w-0">
                        <div className="font-display text-sm text-[#0891b2] leading-tight truncate">
                          {lang === "ar" ? c.name_ar : c.name_en}
                        </div>
                        {c.primary ? (
                          <div className="text-[9px] uppercase tracking-widest text-[#ef4444] font-semibold mt-0.5">
                            {g.homeBase}
                          </div>
                        ) : (
                          <div className="text-[10px] text-slate-500 mt-0.5">{g.activeMarket}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Beyond GCC */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#ef4444] flex items-center justify-center">
                  <Globe2 className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold uppercase">
                    {g.beyondEyebrow}
                  </div>
                  <div className="font-display text-lg text-[#0891b2]">{g.beyondTitle}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {BEYOND.map((c) => (
                  <div
                    key={c.code}
                    data-testid={`gr-beyond-${c.code}`}
                    className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-[#0891b2] rounded-full px-3 py-1.5 text-xs transition-all"
                  >
                    <span className="text-base leading-none">{c.flag}</span>
                    <span className="font-medium text-slate-700">
                      {lang === "ar" ? c.name_ar : c.name_en}
                    </span>
                  </div>
                ))}
                <div className="inline-flex items-center gap-2 bg-[#0891b2] text-white rounded-full px-3 py-1.5 text-xs font-semibold">
                  <Globe2 className="w-3 h-3" />
                  {g.morePlus}
                </div>
              </div>
            </div>

            {/* Bottom stats */}
            <div className="mt-8 pt-8 border-t border-slate-200 grid grid-cols-3 gap-4">
              <div data-testid="gr-stat-0">
                <div className="font-display text-3xl text-[#0891b2]">15+</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{g.stat1}</div>
              </div>
              <div data-testid="gr-stat-1">
                <div className="font-display text-3xl text-[#ef4444]">100+</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{g.stat2}</div>
              </div>
              <div data-testid="gr-stat-2">
                <div className="font-display text-3xl text-[#0891b2]">24/7</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{g.stat3}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
